import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Appointment } from '../../../../core/models';
import { AppointmentService } from '../../../../core/services/appointment.service';
import { SearchBarComponent } from '../../../../features/dashboard/components/search-bar/search-bar.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SearchBarComponent],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  @Input() doctorName: string | null = null;
  @Input() showSearch = true;
  isMobileSearchVisible = false;
  searchTerm: string = '';
  searchResults: Appointment[] = [];
  private router = inject(Router);
  @ViewChild(SearchBarComponent) searchBarComponent!: SearchBarComponent;

  private appointmentService = inject(AppointmentService);

  ngOnInit(): void {
    // Optionally pre-fetch all appointments if search is always active
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    if (this.searchTerm && this.searchTerm.length > 2) {
      this.appointmentService.getAppointments().subscribe((appointments: Appointment[]) => {
        const filteredAppointments = appointments.filter(appointment =>
          appointment.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          appointment.caseCategory.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          appointment.caseType.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
        this.searchResults = this.consolidateResults(filteredAppointments);
      });
    } else {
      this.searchResults = [];
    }
  }

  private consolidateResults(appointments: Appointment[]): Appointment[] {
    const patientMap = new Map<string, Appointment>();
    appointments.forEach(appointment => {
      if (!appointment.patientKey) { return; } // Explicitly handle missing key
      const existing = patientMap.get(appointment.patientKey);
      if (!existing) {
        patientMap.set(appointment.patientKey, appointment);
        return;
      }
      const now = new Date();
      const existingDate = existing.start ? new Date(existing.start) : new Date(0);
      const currentAppointmentDate = appointment.start ? new Date(appointment.start) : new Date(0);
      const isExistingFuture = existingDate > now;
      const isCurrentFuture = currentAppointmentDate > now;

      if (isCurrentFuture && !isExistingFuture) {
        // Current is future, existing is past. Choose current.
        patientMap.set(appointment.patientKey, appointment);
      } else if (isCurrentFuture && isExistingFuture) {
        // Both are future. Choose the soonest.
        if (currentAppointmentDate < existingDate) {
          patientMap.set(appointment.patientKey, appointment);
        }
      } else if (!isCurrentFuture && !isExistingFuture) {
        // Both are past. Choose the most recent.
        if (currentAppointmentDate > existingDate) {
          patientMap.set(appointment.patientKey, appointment);
        }
      } else {
        // Fallback for equal dates or other edge cases. Use updateTime.
        const existingUpdateTime = existing.updateTime ? new Date(existing.updateTime) : new Date(0);
        const currentUpdateTime = appointment.updateTime ? new Date(appointment.updateTime) : new Date(0);
        if (currentUpdateTime > existingUpdateTime) {
          patientMap.set(appointment.patientKey, appointment);
        }
      }
    });
    return Array.from(patientMap.values());
  }

  toggleMobileSearch(): void {
    this.isMobileSearchVisible = !this.isMobileSearchVisible;
    if (this.isMobileSearchVisible) {
      // Focus the search input when it becomes visible
      // Use setTimeout to ensure the input is rendered before focusing
      setTimeout(() => {
        this.searchBarComponent.focusInput();
      }, 0);
    } else {
      this.searchResults = [];
      this.searchTerm = '';
    }
  }

  onSearchBlur(): void {
    // Use a setTimeout to allow click events on search results to fire before clearing
    setTimeout(() => {
      this.searchResults = [];
    }, 100);
  }

  closeCaseDetails(): void {
    const navigatePath = '/app/dashboard';
    this.router.navigate([navigatePath]);
  }

  navigateToResult(result: Appointment): void {
    this.router.navigate(['/case-details', result.patientKey], { queryParams: { orgCode: result.orgCode } });
  }
}